/**
 * mainGamePlayUrl — the clean address bar on the main game domain.
 *
 * helix2000.com players should see `helix2000.com/play`, not
 * `/?map=drowned_road&preset=live-lobby&skipSplash=1&keepCharacter=1…`.
 * PTR, localhost, QA tunnels and the legacy prod host h2000.djmsqrvve.com
 * are not main-game domains (environment.ts MAIN_GAME_HOSTNAMES) and keep
 * their long config URLs.
 *
 * Lifecycle of one page:
 *   1. The page loads with its full URL (a portal or the roster still
 *      navigates with params — they are the page's input).
 *   2. Every boot reader runs against that URL.
 *   3. When the game scene is ready, `cleanMainGameAddressBar()` freezes the
 *      boot URL (bootUrl.ts — late readers keep seeing it) and replaces the
 *      address with `/play`, stashing the boot URL in `history.state`.
 *   4. A reload of `/play` keeps that history entry's state, so
 *      `computePlayRestore()` (run by runMainGameBoot, the FIRST boot import) puts the boot
 *      URL back before any reader evaluates — the reload boots exactly like
 *      a reload of the long URL did. A `/play` with no stash (a shared link,
 *      a new tab) goes to `/resume`, which never redirects: no loop.
 */
import { BOOT_URL_STATE_KEY, freezeBootUrl, isBootUrlFrozen } from './bootUrl';
import { isMainGameDomain } from '../environment/environment';

export const MAIN_GAME_PLAY_PATH = '/play';
export const MAIN_GAME_PLAY_FALLBACK = '/resume';

/** A stashed boot URL we are willing to restore: same-origin path, not /play. */
function isRestorablePath(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 4096) return false;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return false;
  const path = value.split(/[?#]/, 1)[0];
  return path !== MAIN_GAME_PLAY_PATH;
}

export function readStashedBootUrl(historyState: unknown): string | null {
  if (!historyState || typeof historyState !== 'object') return null;
  const value = (historyState as Record<string, unknown>)[BOOT_URL_STATE_KEY];
  return isRestorablePath(value) ? value : null;
}

export type PlayRestore =
  | { kind: 'restore'; url: string }
  | { kind: 'redirect'; url: string };

/**
 * Decide what a load of `/play` does. Null for every other path and on every
 * non-main-game domain.
 */
export function computePlayRestore(
  pathname: string,
  historyState: unknown,
  isMainGame: boolean,
): PlayRestore | null {
  if (!isMainGame || pathname !== MAIN_GAME_PLAY_PATH) return null;
  const stashed = readStashedBootUrl(historyState);
  return stashed
    ? { kind: 'restore', url: stashed }
    : { kind: 'redirect', url: MAIN_GAME_PLAY_FALLBACK };
}

/**
 * Freeze the boot URL and show `/play`. Main game domain only; idempotent
 * (a second scene-ready never freezes the already-clean path). Returns true
 * when it rewrote the address bar.
 */
export function cleanMainGameAddressBar(isMainGame: boolean = isMainGameDomain()): boolean {
  if (!isMainGame || typeof window === 'undefined') return false;
  if (isBootUrlFrozen()) return false;
  const bootUrl = freezeBootUrl();
  const state = window.history.state && typeof window.history.state === 'object'
    ? (window.history.state as Record<string, unknown>)
    : {};
  window.history.replaceState({ ...state, [BOOT_URL_STATE_KEY]: bootUrl }, '', MAIN_GAME_PLAY_PATH);
  return true;
}
